// Hideo - Google Drive: sign-in (Google Identity Services, token kept until it expires), the Drive API calls, the "Hideo"
// folder and its "Immagini" / "Vocali" subfolders, multipart uploads, and the service object (CLOUD_GOOGLE).
// Scope drive.file: the app sees only the files it created. Ported from the legacy cloud/cloud.js (verbatim copy in
// tests/fixtures/legacy/cloud.js, golden tests in tests/saving*.test.ts): same requests in the same order.
import { t } from '../../i18n'
import { CONFIG } from '../../config'
import { CLOUD_FOLDER, CS, MEDIA_FOLDERS, cloudErr, cloudOn, saveDriveState, tokenFresh, type CloudError, type CloudMeta, type CloudService, unless404 } from './common'

export const GDRIVE = {
  /** From VITE_GOOGLE_CLIENT_ID (src/config.ts) */
  clientId: CONFIG.googleClientId,
  scope: 'https://www.googleapis.com/auth/drive.file',
  api: 'https://www.googleapis.com/drive/v3',
  upload: 'https://www.googleapis.com/upload/drive/v3'
}

/** The Google sign-in library (accounts.google.com/gsi/client), once loaded */
interface GoogleTokenResponse { access_token: string; expires_in?: number; error?: string; error_description?: string }
interface GoogleAccounts {
  accounts: {
    oauth2: {
      initTokenClient(cfg: {
        client_id: string; scope: string; callback: (r: GoogleTokenResponse) => void; error_callback: (e: { type?: string } | null) => void
      }): { requestAccessToken(o: Record<string, string>): void }
      hasGrantedAllScopes(r: GoogleTokenResponse, scope: string): boolean
      revoke(token: string, done: () => void): void
    }
  }
}
const gis = () => (window as unknown as { google?: GoogleAccounts }).google

/** Google access token: value and expiry (ms) */
interface GoogleToken { value: string; exp: number }
// The Google sign-in (~1 hour) survives a page reload (key creatorOS_gtoken, only until it expires)
let gisLoad: Promise<unknown> | null = null
let gToken: GoogleToken | null = (() => { try { const saved = JSON.parse(localStorage.getItem('creatorOS_gtoken') as string); return saved && saved.exp > Date.now() ? saved : null } catch { return null } })()
/** The Google token of this session, or null */
export const googleTokenNow = (): GoogleToken | null => gToken
export function setGToken(token: GoogleToken | null): void { gToken = token; try { if (token) localStorage.setItem('creatorOS_gtoken', JSON.stringify(token)); else localStorage.removeItem('creatorOS_gtoken') } catch { /* blocked storage */ } }
/** Loads Google's sign-in library (once; again after a failure) */
export function loadGis(): Promise<unknown> {
  if (!gisLoad) gisLoad = new Promise((res, rej) => {
    const s = document.createElement('script'); s.src = 'https://accounts.google.com/gsi/client'; s.async = true
    s.onload = res; s.onerror = () => { gisLoad = null; rej(new Error(t("Can't reach Google: are you connected to the internet?"))) }
    document.head.appendChild(s)
  })
  return gisLoad
}
/** The Google token: the one of this session while valid; otherwise (interactive only, inside a click) Google's window */
async function googleToken(interactive: boolean): Promise<string | null> {
  if (gToken && tokenFresh(gToken.exp)) return gToken.value
  if (!interactive) return null   // the Google sign-in can only be renewed with a click
  await loadGis()
  return new Promise((res, rej) => {
    const google = gis() as GoogleAccounts
    google.accounts.oauth2.initTokenClient({
      client_id: GDRIVE.clientId, scope: GDRIVE.scope,
      callback: r => {
        if (r.error) return rej(new Error(t('Google sign-in failed ({error}).', { error: r.error_description || r.error })))
        if (!google.accounts.oauth2.hasGrantedAllScopes(r, GDRIVE.scope)) return rej(new Error(t("You didn't give permission for Google Drive: try again and tick the Drive box.")))
        setGToken({ value: r.access_token, exp: Date.now() + (r.expires_in || 3600) * 1000 }); res((gToken as GoogleToken).value)
      },
      error_callback: e => rej(new Error(e && e.type === 'popup_failed_to_open' ? t('Your browser blocked the Google window: allow pop-ups for this site and try again.')
        : e && e.type === 'popup_closed' ? t('Google sign-in cancelled.') : t('Google sign-in failed.')))
    }).requestAccessToken({ prompt: cloudOn('google') ? '' : 'consent', ...(CS('google').email ? { login_hint: CS('google').email as string } : {}) })
  })
}
async function gapi(path: string, opt: RequestInit = {}, base = GDRIVE.api): Promise<Response> {
  const token = await googleToken(true)
  const r = await fetch(base + path, { ...opt, headers: { Authorization: 'Bearer ' + token, ...((opt.headers as Record<string, string>) || {}) } })
  if (r.status === 401) { setGToken(null); throw cloudErr(t('Your {service} sign-in has expired: please try again.', { service: 'Google' }), 401) }
  if (!r.ok) throw cloudErr(t('{service} returned an error ({status}).', { service: 'Google Drive', status: r.status }), r.status, await r.text())
  return r
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const gjson = async (path: string, opt?: RequestInit, base?: string): Promise<any> => (await gapi(path, opt, base)).json()
const gq = (s: string) => encodeURIComponent(s)
// Folders created under the app's former names are found and renamed "Hideo". The files inside keep the old prefix until
// the profile is saved again (driveSave finds them by profileId, not by name, and renames them "Hideo - <profile>.json")
const DRIVE_OLD_FOLDERS = ['Flagenda', 'Ideo', 'PedlyFlag', 'Pedly', 'CreatorOS']
interface DriveFile { id: string; name: string; trashed?: boolean; modifiedTime?: string; appProperties?: Record<string, string> }
async function driveRenameOldFolder(f: DriveFile | undefined): Promise<void> {
  if (f && DRIVE_OLD_FOLDERS.includes(f.name)) { try { await gjson(`/files/${f.id}?fields=id`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: CLOUD_FOLDER }) }) } catch (e) { console.warn(e) } }
}
/** The "Hideo" folder: the known one if still there, otherwise found by name (or under a former name) or created */
async function gFolder(): Promise<string> {
  const G = CS('google')
  if (G.folderId) {
    try { const f = await gjson(`/files/${G.folderId}?fields=id,name,trashed`); if (!f.trashed) { await driveRenameOldFolder(f); return f.id } }
    catch (e) { if ((e as CloudError).status !== 404) throw e }
  }
  const list: DriveFile[] = (await gjson(`/files?q=${gq(`mimeType='application/vnd.google-apps.folder' and (${[CLOUD_FOLDER, ...DRIVE_OLD_FOLDERS].map(n => `name='${n}'`).join(' or ')}) and trashed=false`)}&fields=files(id,name)`)).files || []
  const found = [CLOUD_FOLDER, ...DRIVE_OLD_FOLDERS].map(n => list.find(f => f.name === n)).find(Boolean)
  await driveRenameOldFolder(found)
  const id = found ? found.id : (await gjson('/files?fields=id', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: CLOUD_FOLDER, mimeType: 'application/vnd.google-apps.folder' }) })).id
  G.folderId = id; saveDriveState(); return id
}
const G_FIELDS = 'id,name,trashed,modifiedTime,appProperties'
const gNorm = (f: DriveFile): CloudMeta => { const ap = f.appProperties || {}; return { id: f.id, name: f.name, modified: f.modifiedTime, savedAt: ap.savedAt || '', savedOn: ap.savedOn || '', version: ap.savedAt || f.modifiedTime } }
// Subfolders checked in this session
const gSub: Record<string, boolean> = {}
async function gSubfolder(kind: 'img' | 'voice'): Promise<string> {
  const G = CS('google'); G.sub = G.sub || {}
  if (G.sub[kind] && gSub[kind]) return G.sub[kind]
  const parent = await gFolder(), name = MEDIA_FOLDERS[kind]
  if (G.sub[kind]) {
    try { const f = await gjson(`/files/${G.sub[kind]}?fields=id,trashed`); if (!f.trashed) { gSub[kind] = true; return f.id } }
    catch (e) { if ((e as CloudError).status !== 404) throw e }
  }
  const found = (await gjson(`/files?q=${gq(`mimeType='application/vnd.google-apps.folder' and name='${name}' and '${parent}' in parents and trashed=false`)}&fields=files(id)`)).files[0]
  const id = found ? found.id : (await gjson('/files?fields=id', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, mimeType: 'application/vnd.google-apps.folder', parents: [parent] }) })).id
  G.sub[kind] = id; gSub[kind] = true; saveDriveState(); return id
}
async function gMultipart(fileId: string | null | undefined, meta: Record<string, unknown>, body: string | Blob, type: string): Promise<DriveFile> {
  const b = 'pedly' + Date.now()
  const data = new Blob([`--${b}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(meta)}\r\n--${b}\r\nContent-Type: ${type}\r\n\r\n`, body, `\r\n--${b}--`])
  return gjson(fileId ? `/files/${fileId}?uploadType=multipart&fields=${G_FIELDS}` : `/files?uploadType=multipart&fields=${G_FIELDS}`,
    { method: fileId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'multipart/related; boundary=' + b }, body: data }, GDRIVE.upload)
}

export const CLOUD_GOOGLE: CloudService = {
  id: 'google', name: 'Google Drive', icon: 'fab fa-google-drive', place: t('the "Hideo" folder in your Drive'), configured: () => true,
  token: googleToken,
  async connect() {
    await googleToken(true)
    let email = ''; try { email = ((await gjson('/about?fields=user(emailAddress)')).user || {}).emailAddress || '' } catch { /* the email is optional */ }
    await gFolder(); return { email }
  },
  disconnect() { const google = gis(); if (gToken && google) { try { google.accounts.oauth2.revoke(gToken.value, () => {}) } catch { /* already revoked */ } } setGToken(null) },
  async listProfiles() {
    const folder = await gFolder()
    return ((await gjson(`/files?q=${gq(`'${folder}' in parents and trashed=false and mimeType!='application/vnd.google-apps.folder'`)}&orderBy=modifiedTime desc&pageSize=100&fields=files(${G_FIELDS})`)).files || []).map(gNorm)
  },
  async meta(id) { return unless404(async () => { const f = await gjson(`/files/${id}?fields=${G_FIELDS}`); return f.trashed ? null : gNorm(f) }, null) },
  async findProfile(_fileName, profileId) {
    const folder = await gFolder()
    const f = ((await gjson(`/files?q=${gq(`'${folder}' in parents and trashed=false and appProperties has { key='profileId' and value='${profileId}' }`)}&fields=files(${G_FIELDS})`)).files || [])[0]
    return f ? gNorm(f) : null
  },
  async saveProfile(id, fileName, text, info) {
    const meta: { name: string; mimeType: string; appProperties: Record<string, string>; parents?: string[] } = { name: fileName, mimeType: 'application/json', appProperties: { savedAt: info.savedAt, savedOn: info.savedOn } }
    if (!id) { meta.parents = [await gFolder()]; meta.appProperties.profileId = info.profileId }
    return gNorm(await gMultipart(id, meta, text, 'application/json'))
  },
  async readText(id) { return (await gapi(`/files/${encodeURIComponent(id)}?alt=media`)).text() },
  async findMedia(mediaId) { return ((await gjson(`/files?q=${gq(`appProperties has { key='mediaId' and value='${mediaId}' } and trashed=false`)}&fields=files(id)`)).files[0] || {}).id || '' },
  async uploadMedia(kind, fileName, blob, mediaId) {
    return (await gMultipart(null, { name: fileName, mimeType: blob.type || undefined, parents: [await gSubfolder(kind)], appProperties: { mediaId } }, blob, blob.type || 'application/octet-stream')).id
  },
  async readBlob(id) { const r = await gapi(`/files/${encodeURIComponent(id)}?alt=media`); return { buf: await r.arrayBuffer(), type: (r.headers.get('Content-Type') || '').split(';')[0] } }
}
